import { MapPin, Clock, CalendarDays, ExternalLink, IdCard, UserPlus, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { VOTING_WARDS, ELECTION_DATES } from "@/data/content";
import { Marked, Asterisk } from "@/components/campaign/HandDrawn";

const fmtDate = (iso) => {
  const d = new Date(iso + "T12:00:00");
  return {
    full: d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
    short: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  };
};

const mapLink = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

const Vote = () => {
  return (
    <main data-testid="vote-page">
      {/* HERO */}
      <section className="section border-b-4 border-ink">
        <div className="container-x grid gap-12 md:grid-cols-12 items-end">
          <div className="md:col-span-8">
            <p className="overline inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> Where to vote
            </p>
            <h1 className="mt-4 font-editorial font-semibold text-5xl md:text-7xl tracking-tight leading-[0.92] text-ink">
              Show up. <br />
              Bring a <Marked>friend</Marked>.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink/75 leading-relaxed text-lg">
              Voting in Manchester is quick — most polling places take fewer than 10 minutes.
              Below: your polling place, key dates, and everything you need to register.
            </p>
          </div>
        </div>
      </section>

      {/* KEY DATES */}
      <section className="section bg-parchment" data-testid="election-dates-section">
        <div className="container-x">
          <p className="overline inline-flex items-center gap-2">
            <CalendarDays className="h-4 w-4" /> 2026 Election Dates
          </p>
          <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-6xl leading-[0.95] text-ink tracking-tight">
            Mark your <Marked>calendar</Marked>.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {ELECTION_DATES.map((e) => {
              const d = fmtDate(e.date);
              return (
                <article
                  key={e.date}
                  className="bg-white/5 border border-rule rounded-2xl p-8"
                  data-testid={`election-date-${e.date}`}
                >
                  <p className="overline">{e.title}</p>
                  <p className="mt-4 font-editorial font-semibold text-3xl text-ink">{d.full}</p>
                  <p className="mt-2 text-sm text-ink/60 font-medium inline-flex items-center gap-2">
                    <Clock size={14} className="text-terracotta" /> Polls open 6:00 AM – 7:00 PM
                  </p>
                  <p className="mt-5 text-ink/80 leading-relaxed">{e.detail}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WARD POLLING LOCATIONS */}
      <section className="section" data-testid="ward-locations-section">
        <div className="container-x">
          <p className="overline inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Hillsborough 40 · Polling Places
          </p>
          <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-6xl leading-[0.95] text-ink tracking-tight">
            Your ward, your <Marked>polling place</Marked>.
          </h2>
          <p className="mt-6 text-ink/75 leading-relaxed max-w-2xl text-lg">
            Hillsborough 40 covers Manchester Wards 1, 3, 10, 11, and 12. Not sure which ward
            you&apos;re in?{" "}
            <a
              href="https://www.mymanchesternh.com/Resources/Find-Your-Ward-Alderman-Voting-Location"
              target="_blank"
              rel="noreferrer"
              className="text-terracotta font-semibold underline decoration-2 underline-offset-4 inline-flex items-center gap-1"
            >
              Look it up by address <ExternalLink size={14} />
            </a>
            .
          </p>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {VOTING_WARDS.map((w) => (
              <article
                key={w.ward}
                className="relative bg-white/5 border border-rule rounded-2xl p-8 transition-colors duration-300 hover:border-terracotta/60"
                data-testid={`ward-card-${w.ward}`}
              >
                <div className="flex items-baseline justify-between">
                  <p className="overline">Ward</p>
                  <span className="font-editorial font-semibold text-6xl text-terracotta leading-none">
                    {w.ward}
                  </span>
                </div>
                <h3 className="mt-6 font-editorial font-semibold text-2xl text-ink leading-tight">
                  {w.name}
                </h3>
                <p className="mt-3 text-ink/70 text-sm flex items-start gap-2">
                  <MapPin size={14} className="text-terracotta shrink-0 mt-0.5" />
                  <span>{w.address}</span>
                </p>
                {w.note && (
                  <p className="mt-4 text-sm bg-terracotta/10 border border-terracotta/40 text-ink px-3 py-2 rounded-md">
                    Note: {w.note}
                  </p>
                )}
                <a
                  href={mapLink(w.mapQuery)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-1.5 text-ink font-semibold uppercase tracking-wide text-xs hover:text-terracotta border-b-2 border-ink pb-0.5"
                  data-testid={`ward-map-${w.ward}`}
                >
                  Directions <ExternalLink size={12} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* VOTER REGISTRATION */}
      <section className="section bg-parchment" data-testid="registration-section">
        <div className="container-x grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="overline inline-flex items-center gap-2">
              <UserPlus className="h-4 w-4" /> Registration
            </p>
            <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-5xl leading-[0.95] text-ink tracking-tight">
              Not registered? <br />
              <Marked>You still can be.</Marked>
            </h2>
            <p className="mt-6 text-ink/80 leading-relaxed text-lg">
              New Hampshire allows same-day voter registration at your polling place on
              Election Day. You can also register in advance at the Manchester City
              Clerk&apos;s office.
            </p>
            <a
              href="https://www.manchesternh.gov/departments/city-clerk/voter-registration-and-elections"
              target="_blank"
              rel="noreferrer"
              className="mt-8 btn-primary"
              data-testid="clerk-link"
            >
              Manchester City Clerk <ArrowUpRight size={18} />
            </a>
          </div>
          <div className="md:col-span-7">
            <div className="bg-white/5 border border-rule rounded-2xl p-8">
              <p className="overline">Deadlines</p>
              <ul className="mt-5 space-y-4 text-ink/85">
                <li className="flex gap-4">
                  <span className="font-editorial font-semibold text-2xl text-terracotta shrink-0">01</span>
                  <span>
                    <span className="font-semibold">Same-day at your polling place</span> — bring
                    proof of identity, age, citizenship, and Manchester residence.
                  </span>
                </li>
                <li className="flex gap-4">
                  <span className="font-editorial font-semibold text-2xl text-terracotta shrink-0">02</span>
                  <span>
                    <span className="font-semibold">Mail-in registration:</span> deadline is
                    ~13 days before Election Day. Request a form from the city clerk.
                  </span>
                </li>
                <li className="flex gap-4">
                  <span className="font-editorial font-semibold text-2xl text-terracotta shrink-0">03</span>
                  <span>
                    <span className="font-semibold">In person before Election Day:</span> the
                    Manchester Supervisors of the Checklist meet within a 6–13 day window
                    before each election — call the clerk for the exact date.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT TO BRING */}
      <section className="section" data-testid="what-to-bring-section">
        <div className="container-x grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="overline inline-flex items-center gap-2">
              <IdCard className="h-4 w-4" /> At the polls
            </p>
            <h2 className="mt-4 font-editorial font-semibold text-4xl md:text-5xl leading-[0.95] text-ink tracking-tight">
              What to <Marked>bring</Marked>.
            </h2>
            <p className="mt-6 text-ink/80 leading-relaxed text-lg">
              A valid photo ID is required to vote. If you don&apos;t have one on Election
              Day, you can still cast a ballot after signing an affidavit.
            </p>
          </div>
          <div className="md:col-span-7 grid gap-4">
            <div className="bg-white/5 border border-rule rounded-2xl p-6">
              <p className="overline">Acceptable Photo ID</p>
              <ul className="mt-4 space-y-2 text-ink/85 list-disc list-inside marker:text-terracotta">
                <li>NH driver&apos;s license or non-driver photo ID</li>
                <li>Out-of-state driver&apos;s license (with additional verification)</li>
                <li>U.S. passport or passport card</li>
                <li>Military ID</li>
                <li>Federal or state government-issued photo ID</li>
              </ul>
              <p className="mt-5 text-sm text-ink/60 italic">
                Note: as of June 2026, student IDs are no longer accepted at the polls.
              </p>
            </div>
            <div className="bg-white/5 border border-rule rounded-2xl p-6">
              <p className="overline">If you&apos;re registering same-day</p>
              <p className="mt-4 text-ink/85">Bring proof of:</p>
              <ul className="mt-3 space-y-2 text-ink/85 list-disc list-inside marker:text-terracotta">
                <li><span className="font-semibold">Identity</span> — photo ID (see above)</li>
                <li><span className="font-semibold">Age</span> — birth certificate, passport, or driver&apos;s license</li>
                <li><span className="font-semibold">Citizenship</span> — passport, naturalization papers, or birth certificate</li>
                <li><span className="font-semibold">Domicile in Manchester</span> — utility bill, lease, or a signed affidavit</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-cream section relative overflow-hidden">
        <div className="container-x flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <p className="overline !text-terracotta inline-flex items-center gap-2">
              <Asterisk className="h-4 w-4" /> Questions
            </p>
            <h2 className="mt-3 font-editorial font-semibold text-4xl md:text-5xl leading-[0.95]">
              Still not sure? <br />
              <Marked color="#F58A3F">Ask the campaign.</Marked>
            </h2>
          </div>
          <Link to="/contact" className="btn-primary" data-testid="vote-contact-btn">
            Contact us <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Vote;
